export type ViewBox = {
  minX: number;
  minY: number;
  width: number;
  height: number;
};

export type ParsedPath = {
  d: string;
  stroke?: string;
  strokeWidth?: number;
  strokeDasharray?: string;
  fill?: string;
  opacity?: number;
};

export type ParsedViewData = {
  paths: ParsedPath[];
  viewBox: ViewBox;
};

function lineToPath(el: Element): string {
  const x1 = el.getAttribute('x1') || '0';
  const y1 = el.getAttribute('y1') || '0';
  const x2 = el.getAttribute('x2') || '0';
  const y2 = el.getAttribute('y2') || '0';
  return `M${x1},${y1}L${x2},${y2}`;
}

function rectToPath(el: Element): string {
  const x = parseFloat(el.getAttribute('x') || '0');
  const y = parseFloat(el.getAttribute('y') || '0');
  const w = parseFloat(el.getAttribute('width') || '0');
  const h = parseFloat(el.getAttribute('height') || '0');
  return `M${x},${y}H${x + w}V${y + h}H${x}Z`;
}

function circleToPath(el: Element): string {
  const cx = parseFloat(el.getAttribute('cx') || '0');
  const cy = parseFloat(el.getAttribute('cy') || '0');
  const r = parseFloat(el.getAttribute('r') || '0');
  return `M${cx - r},${cy}A${r},${r},0,1,0,${cx + r},${cy}A${r},${r},0,1,0,${cx - r},${cy}Z`;
}

function ellipseToPath(el: Element): string {
  const cx = parseFloat(el.getAttribute('cx') || '0');
  const cy = parseFloat(el.getAttribute('cy') || '0');
  const rx = parseFloat(el.getAttribute('rx') || '0');
  const ry = parseFloat(el.getAttribute('ry') || '0');
  return `M${cx - rx},${cy}A${rx},${ry},0,1,0,${cx + rx},${cy}A${rx},${ry},0,1,0,${cx - rx},${cy}Z`;
}

function polygonToPath(el: Element): string {
  const points = el.getAttribute('points') || '';
  if (!points.trim()) return '';
  return `M${points.trim().replace(/\s+/g, 'L')}Z`;
}

function polylineToPath(el: Element): string {
  const points = el.getAttribute('points') || '';
  if (!points.trim()) return '';
  return `M${points.trim().replace(/\s+/g, 'L')}`;
}

function extractPathData(el: Element): ParsedPath | null {
  let d: string;
  const tag = el.tagName.toLowerCase();

  switch (tag) {
    case 'path':
      d = el.getAttribute('d') || '';
      break;
    case 'line':
      d = lineToPath(el);
      break;
    case 'rect':
      d = rectToPath(el);
      break;
    case 'circle':
      d = circleToPath(el);
      break;
    case 'ellipse':
      d = ellipseToPath(el);
      break;
    case 'polygon':
      d = polygonToPath(el);
      break;
    case 'polyline':
      d = polylineToPath(el);
      break;
    default:
      return null;
  }

  if (!d) return null;

  const stroke = el.getAttribute('stroke') || undefined;
  const strokeWidthStr = el.getAttribute('stroke-width');
  const strokeWidth = strokeWidthStr ? parseFloat(strokeWidthStr) : undefined;
  const strokeDasharray = el.getAttribute('stroke-dasharray') || undefined;
  const fill = el.getAttribute('fill') || undefined;
  const opacityStr = el.getAttribute('opacity');
  const opacity = opacityStr ? parseFloat(opacityStr) : undefined;

  return { d, stroke, strokeWidth, strokeDasharray, fill, opacity };
}

function parseViewBox(svg: SVGSVGElement): ViewBox {
  const vbAttr = svg.getAttribute('viewBox');
  if (vbAttr) {
    const parts = vbAttr.trim().split(/[\s,]+/).map(Number);
    if (parts.length === 4 && parts.every((n) => !isNaN(n))) {
      return { minX: parts[0], minY: parts[1], width: parts[2], height: parts[3] };
    }
  }
  const w = parseFloat(svg.getAttribute('width') || '100');
  const h = parseFloat(svg.getAttribute('height') || '100');
  return { minX: 0, minY: 0, width: w, height: h };
}

function collectElements(el: Element): Element[] {
  const result: Element[] = [];
  const shapeTypes = new Set(['path', 'line', 'rect', 'circle', 'ellipse', 'polygon', 'polyline']);
  for (const child of Array.from(el.children)) {
    if (shapeTypes.has(child.tagName.toLowerCase())) {
      result.push(child);
    }
    // Recurse into groups
    if (child.tagName.toLowerCase() === 'g') {
      result.push(...collectElements(child));
    }
  }
  return result;
}

export function parseSvgToStructuredData(svgString: string): ParsedViewData | null {
  if (!svgString) return null;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgString, 'image/svg+xml');
    const svg = doc.querySelector('svg');
    if (!svg) return null;

    const viewBox = parseViewBox(svg);
    const elements = collectElements(svg);
    const paths: ParsedPath[] = [];

    for (const el of elements) {
      const parsed = extractPathData(el);
      if (parsed) {
        paths.push(parsed);
      }
    }

    return { paths, viewBox };
  } catch {
    return null;
  }
}
